// MONEY MOVEMENTS, REBUILT (UI rebuild batch 2; drawn when lib/ui-v2.ts switches batch 2 on). One job: reconcile the
// money that actually moved. KEEP THE BEHAVIOUR, CHANGE THE LAYOUT — every read, key, guard, deep link and door below is
// screens/Movements.tsx's, copied (its header says why each one is as it is); what changed is what is drawn by default:
//
//   head       "Money movements", and Record money — the screen's one gold primary
//   bar        search first (reference, note or chain id — what the route searches), the period chips, and ONE Filters
//              button holding the rarer ones: Type, Asset, Party, Account, Show reversed (MovementFilters)
//   lines      the reversed-entry line and a failed refresh — one line each, above the list
//   totals     one compact strip at the head of the list (restored 1 Nov 2026): per currency, in · out · closing over
//              EVERY match, never the page; the Totals link keeps the opening and the window caveat (TotalsSheet)
//   footer     Astra's: "N records · Newest first"; a cut page says "more below" with Load more; and ONE
//              quiet link, Totals — opening, in, out and closing per currency over EVERY match, never the page, with
//              the window caveat as its first line (TotalsSheet)
//   list       one row per entry, about the money (MovementRegister); with the account filter naming ONE account, that
//              account's statement instead — the same register, the balance after each line in the Account seat (StatementRegister)
//   receipt    a row opens its receipt (EntrySheet): who, how much, which way, which account, the reference, the time;
//              the ledger's sentence and raw legs behind a disclosure; the Reverse door as today. The open entry is in
//              the URL (?entry=<txn>), so Back closes it and the list underneath never moved.
//
// THE DEEP LINKS (`/movements?party&rail&asset&kind&from&to&q`) read once, as the initial state, exactly as today; a
// link to ONE entry is `?q=<reference>` — there is no GET for one entry — and when that reference names exactly one
// loaded row the receipt opens on it.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { DeskSwitch, useDesk } from '../../components/DeskSwitch';
import { ApiError } from '../../lib/errors';
import { offlineFailure } from '../../lib/read-state';
import {
  EmptyState, PageHead, Panel, Pending, SearchBar, SkeletonRows, StateLine,
} from '../../components/v2/kit';
import { periodDays, periodWords, type PeriodValue } from '../../components/PeriodChips';
import { PeriodButton, PeriodPanel } from '../../components/v2/movements/PeriodControl';
import type { ComboboxOption } from '../../components/Combobox';
import { DealSlideOver } from '../../components/DealSlideOver';
// Today's ReadFailed (the `says` form: what the screen still shows over a failed read), not the kit's re-export.
import { LAST_READ_NONE, ReadFailed } from '../../components/OnboardingParts';
import type { MovementLineRow } from '../../components/MovementTable';
import { ReversedLine, type ReversedOutcome } from '../../components/ReverseEntry';
import { api, type MovementKindFilter, type MovementTotal, type TradeRow } from '../../lib/api';
import { displayAsset, fmtAmount } from '../../lib/format';
import {
  partyName, partySubtitle, railName, railKindGroupLabel,
  needsNameReview, isStale, compareRelevance, foldAsset,
} from '../../lib/naming';
import { ASSETS } from '../../lib/workflow';
import { MovementFilters } from '../../components/v2/movements/MovementFilters';
import { MovementRegister } from '../../components/v2/movements/MovementRegister';
import { TotalsSheet } from '../../components/v2/movements/TotalsSheet';
import { StatementRegister } from '../../components/v2/movements/StatementRegister';
import { EntryView } from '../../components/v2/movements/EntryView';
import { dealOf } from '../../components/v2/movements/words';

/** A hundred movements a page, on a keyset cursor (today's PAGE). */
const PAGE = 100;
/** The trades log's own page, read only to name the deal behind a row — the same key TradesLog uses, one cache entry. */
const TRADE_PAGE = 200;

/** Every kind the route serves, plus "don't filter" (today's KIND_FILTERS). */
const KIND_FILTERS: MovementKindFilter[] =
  ['all', 'trade', 'money_in', 'money_out', 'rail_move', 'p2p', 'adjustment', 'expense', 'owner'];

/** The desk's asset vocabulary, FOLDED — one option per thing the operator can filter on (today's ASSET_FILTERS). */
const ASSET_FILTERS: string[] = [...new Set(ASSETS.map((a) => foldAsset(a)))];

/** Typing must not fire a read per keystroke — the search runs server-side. */
function useDebounced<T>(value: T, ms = 350): T {
  const [held, setHeld] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setHeld(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return held;
}

export function MovementsV2() {
  // ── THE FILTERS, read ONCE from the link (today's rules, line for line) ──────────────────────────────────────────
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [entity, setEntity] = useDesk();
  const [kind, setKind] = useState<MovementKindFilter>(() => {
    const k = params.get('kind') as MovementKindFilter | null;
    return k && KIND_FILTERS.includes(k) ? k : 'all';
  });
  const [partyTyped, setPartyTyped] = useState(() => params.get('party') ?? '');
  const [railTyped, setRailTyped] = useState(() => params.get('rail') ?? '');
  const [asset, setAsset] = useState(() => foldAsset(params.get('asset') ?? ''));
  // A history door (a party's statement, an account on Treasury, a reference) opens on All time; the bare screen on today.
  const [period, setPeriod] = useState<PeriodValue>(() => {
    const from = params.get('from') ?? '', to = params.get('to') ?? '';
    if (from || to) return { key: 'custom', from, to };
    return params.get('party') || params.get('rail') || params.get('q') ? { key: 'all' } : { key: 'today' };
  });
  const [typed, setTyped] = useState(() => params.get('q') ?? '');
  const [undone, setUndone] = useState(false);
  // The period's chips open beneath the bar from its one button (Astra's date control).
  const [periodOpen, setPeriodOpen] = useState(false);
  const search = useDebounced(typed.trim());
  const party = useDebounced(partyTyped.trim());
  const rail = useDebounced(railTyped.trim());
  const win = periodDays(period);

  // ── THE READS (same keys as today's screen, so caches are shared) ────────────────────────────────────────────────
  const parties = useQuery({
    queryKey: ['parties', 'movement-filter', entity],
    queryFn: () => api.listParties({ entity, pageSize: 500 }),
    staleTime: 60_000,
  });
  const rails = useQuery({
    queryKey: ['treasury', 'slugs', entity],
    queryFn: () => api.treasurySlugs(entity),
    staleTime: 60_000,
  });
  // A PARTY THE BOOKS DO NOT HAVE MUST NOT READ AS "NOTHING MOVED" — asked of the party itself (archived ones included).
  const partyCheck = useQuery({
    queryKey: ['party', party, entity],
    queryFn: () => api.partyDetail(party, entity),
    enabled: !!party,
    retry: false,
  });
  // ONLY a 404 means "no such party"; a dead tunnel is an error, shown as one.
  const partyUnknown = !!party && partyCheck.isError && (partyCheck.error as ApiError)?.status === 404;

  const q = useInfiniteQuery({
    queryKey: ['movements', 'log', entity, kind, party, rail, asset, win.from ?? '', win.to ?? '', search, undone],
    queryFn: ({ pageParam }) => api.movements({
      entity,
      kind,
      party: party || undefined,
      rail: rail || undefined,
      asset: asset || undefined,
      from: win.from,
      to: win.to,
      q: search || undefined,
      limit: PAGE,
      cursor: pageParam || undefined,
      include_undone: undone,
    }),
    initialPageParam: '',
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !partyUnknown,
    refetchOnWindowFocus: true,
  });

  const rows: MovementLineRow[] = useMemo(() => q.data?.pages.flatMap((p) => p.rows) ?? [], [q.data]);

  // The deal behind a row — the trades log's page under its own key; nothing is read until a row names a deal.
  const [dealId, setDealId] = useState<string | null>(null);
  const [reversed, setReversed] = useState<ReversedOutcome | null>(null);
  const trades = useQuery({
    queryKey: ['trades', 'log', entity, TRADE_PAGE],
    queryFn: () => api.listTrades(entity, TRADE_PAGE),
    enabled: rows.some((m) => !!dealOf(m)),
    staleTime: 60_000,
  });
  const dealsById = useMemo(
    () => new Map<string, TradeRow>((trades.data?.trades ?? []).map((t) => [t.deal_id, t])),
    [trades.data],
  );
  // The totals cover the WHOLE filtered set and are identical on every page.
  const totals: MovementTotal[] = q.data?.pages[0]?.totals ?? [];
  const caveat = q.data?.pages[0]?.window_caveat ?? null;
  // The Balance seat stands only once a row has brought `legs` (an older Worker sends none).
  const statement = rows.some((m) => Array.isArray(m.legs));
  // With the account filter naming ONE account, the list is that account's statement, named as the rows name it.
  const oneAccount = useMemo(() => {
    if (!rail) return null;
    for (const m of rows) {
      for (const r of [m.rail, m.counter_rail, m.other_rail]) {
        if (r && r.slug === rail) return railName({ slug: r.slug, label: r.label });
      }
    }
    return railName({ slug: rail });
  }, [rail, rows]);

  // WHICH FAILURE THIS IS DECIDES WHAT MAY STAY ON SCREEN (today's rules: a failed Load more or refocus refetch keeps the
  // rows that were read; only "no page ever landed" is nothing read).
  const nothingRead = q.isError && !q.data;
  const pagingError = q.isFetchNextPageError ? q.error : null;
  const refreshError = q.isError && !!q.data && !q.isFetchNextPageError ? q.error : null;
  const waitingOnParty = !!party && partyCheck.isPending;
  // PENDING, not "loading": a first read opened offline is paused — waiting, never an empty list.
  const loaded = !partyUnknown && !waitingOnParty && !nothingRead && !q.isPending;

  const partyOptions: ComboboxOption[] = useMemo(() => {
    const list = parties.data?.parties ?? [];
    return [...list]
      .filter((p) => p.party_type !== 'own_account')
      .sort(compareRelevance)
      .map((p) => ({
        value: p.slug,
        label: partyName(p),
        secondary: partySubtitle(p, { compact: true }),
        stale: isStale(p),
        review: needsNameReview(p.display_name),
      }));
  }, [parties.data]);
  const railOptions: ComboboxOption[] = useMemo(() => {
    const out: ComboboxOption[] = [];
    for (const g of rails.data?.groups ?? []) {
      for (const s of g.slugs) out.push({ value: s, label: railName({ slug: s }), group: railKindGroupLabel(g.kind) });
    }
    return out;
  }, [rails.data]);
  // A pasted ?asset= outside the vocabulary still filters, so it stands as its own option.
  const assetOptions = useMemo(() => {
    const list = asset && !ASSET_FILTERS.includes(asset) ? [...ASSET_FILTERS, asset] : ASSET_FILTERS;
    return list.map((a) => ({ value: a, label: displayAsset(a) }));
  }, [asset]);

  const filtered = !!(party || rail || asset || search || undone || kind !== 'all' || period.key !== 'today');
  const clearAll = () => {
    setKind('all'); setPartyTyped(''); setRailTyped(''); setAsset('');
    setPeriod({ key: 'today' }); setTyped(''); setUndone(false);
  };
  // What the Filters button counts: the filters behind it (search and the period stand in front).
  const behind = [kind !== 'all', !!asset, !!partyTyped.trim(), !!railTyped.trim(), undone].filter(Boolean).length;

  // ── THE RECEIPT: the open entry is in the URL, so Back closes it ─────────────────────────────────────────────────
  const entryId = params.get('entry');
  const selected = entryId ? rows.find((m) => m.txn_id === entryId) ?? null : null;
  const opener = useRef<HTMLElement | null>(null);
  const openEntry = useCallback((m: MovementLineRow) => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const next = new URLSearchParams(params);
    next.set('entry', m.txn_id);
    setParams(next, { state: { entryPushed: true } });
  }, [params, setParams]);
  const closeEntry = useCallback(() => {
    if ((location.state as { entryPushed?: boolean } | null)?.entryPushed) navigate(-1);
    else {
      const next = new URLSearchParams(params);
      next.delete('entry');
      setParams(next, { replace: true });
    }
    // Focus goes back to the row that opened it (keyboard users land where they were).
    const back = opener.current;
    if (back) setTimeout(() => back.focus(), 0);
  }, [location.state, navigate, params, setParams]);
  // Into the receipt: the kit's DetailSheet takes the focus when it opens and keeps it inside (27 Oct 2026).

  // ?q=<reference>: a link to ONE entry. Once the read the link asked for has answered, a reference that names exactly
  // one loaded row opens its receipt (once — after that the search box is his).
  const linkQ = useRef((params.get('q') ?? '').trim());
  const linkOpened = useRef(false);
  useEffect(() => {
    if (linkOpened.current || !linkQ.current || entryId || !q.isSuccess || search !== linkQ.current) return;
    linkOpened.current = true;
    const want = linkQ.current.toLowerCase();
    const hits = rows.filter((m) => m.reference?.toLowerCase() === want || dealOf(m)?.toLowerCase() === want);
    if (hits.length === 1 && hits[0]) openEntry(hits[0]);
  }, [q.isSuccess, rows, search, entryId, openEntry]);

  const onReversed = (o: ReversedOutcome) => { setReversed(o); closeEntry(); };

  // THE SCOPE OF WHAT IS ON SCREEN, said: the count, the period, the order, a cut page, a refresh on its way.
  const [totalsOpen, setTotalsOpen] = useState(false);
  // ASTRA'S FOOTER LINE: the count and the order (never over a failed refresh — the rows are the last read, not a
  // count), a cut page said and its Load more, the one quiet link to the totals over every match, and the clock.
  const footer = (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
      <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {!refreshError && <span>{rows.length} {rows.length === 1 ? 'record' : 'records'} · Newest first{q.hasNextPage && ' · more below'}</span>}
        {q.hasNextPage && (
          <button type="button" className="tap-44 btn-link text-meta" disabled={q.isFetchingNextPage} onClick={() => { void q.fetchNextPage(); }}>
            {q.isFetchingNextPage ? 'Reading…' : 'Load more'}
          </button>
        )}
        {q.isSuccess && totals.length > 0 && (
          <button type="button" className={`tap-44 btn-link text-meta ${caveat ? '!text-warn' : ''}`} onClick={() => setTotalsOpen(true)}>
            {caveat ? 'Totals · short' : 'Totals'}
          </button>
        )}
      </span>
    </div>
  );

  return (
    <>
      <PageHead
        title="Money movements"
        sub="What actually moved"
        actions={(
          <>
            <DeskSwitch value={entity} onChange={setEntity} />
            <Link to="/record" className="btn-primary px-4 text-body"><Plus className="h-4 w-4" aria-hidden />Record money</Link>
          </>
        )}
      />

      {/* SEARCH FIRST. It names what the route searches — the reference, the note, a chain id — not party names, which
          have their own picker behind Filters. The bar stands in every state, so the filter that caused one can change. */}
      <SearchBar
        value={typed}
        onChange={setTyped}
        placeholder="Search reference or note"
        active={behind}
        extra={<PeriodButton value={period} open={periodOpen} onToggle={() => setPeriodOpen((v) => !v)} />}
        filters={(
          <MovementFilters
            kinds={KIND_FILTERS}
            kind={kind} setKind={setKind}
            assetOptions={assetOptions} asset={asset} setAsset={setAsset}
            partyTyped={partyTyped} setPartyTyped={setPartyTyped} partyOptions={partyOptions}
            partiesLoading={parties.isPending}
            partiesFailed={parties.isError ? <ReadFailed what="the parties" error={parties.error} says={parties.data ? 'showing the last read' : undefined} retry={() => { void parties.refetch(); }} /> : undefined}
            partyNamed={party && partyCheck.data ? <>{partyName(partyCheck.data.party)}{partyCheck.data.party.status !== 'active' && ' · archived'}</> : undefined}
            railTyped={railTyped} setRailTyped={setRailTyped} railOptions={railOptions}
            railsLoading={rails.isPending}
            railsFailed={rails.isError ? <ReadFailed what="the accounts" error={rails.error} says={rails.data ? 'showing the last read' : undefined} retry={() => { void rails.refetch(); }} /> : undefined}
            undone={undone} setUndone={setUndone}
            onClear={clearAll} filtered={filtered}
          />
        )}
      />
      {periodOpen && <PeriodPanel value={period} onChange={setPeriod} onDone={() => setPeriodOpen(false)} />}

      {/* ONE LINE EACH, above the list. A failed refresh keeps the rows that were read and says they are the last read. */}
      {loaded && refreshError && (
        <ReadFailed what="the movements" error={refreshError} says={rows.length === 0 ? LAST_READ_NONE : 'showing the last good read'} retry={() => { void q.refetch(); }} className="mb-4" />
      )}
      {loaded && <ReversedLine outcome={reversed} onDismiss={() => setReversed(null)} className="mb-4" />}

      {partyUnknown ? (
        // Not an error and not an empty ledger — a filter that names nobody, said plainly.
        <StateLine
          tone="warn"
          action={<button type="button" className="btn-link" onClick={() => setPartyTyped('')}>Drop the party filter</button>}
        >
          No party called <span className="break-words font-mono">{party}</span>.
        </StateLine>
      ) : nothingRead ? (
        // A FAILED FIRST READ IS NOT AN EMPTY LEDGER: no totals, no list, no zeroes.
        // Offline, the box is grey: waiting, not failed (lib/read-state.ts offlineFailure; ReadFailed says the wait).
        <div className={`rounded-lg border px-4 py-3 ${offlineFailure(q.error) ? 'border-stroke bg-panel-2' : 'border-warn/25 bg-warn-bg'}`}>
          <ReadFailed what="the movements" error={q.error} retry={() => { void q.refetch(); }} />
        </div>
      ) : waitingOnParty || q.isPending ? (
        // The party filter not resolved yet, or the first read on its way (or paused offline): waiting, never empty.
        <Panel flush>
          <div className="px-4 pt-4 text-tg-hint lg:px-5"><Pending /></div>
          <SkeletonRows rows={4} what="the movements" />
        </Panel>
      ) : (
        <>
          {/* THE LIST. Its scope is said under it (Astra's footer line): how many entries are on screen, for which period,
              newest first, whether more are below — and the clock the times are on. */}
          {rows.length > 0 && (
            <Panel flush>
              {/* THE TOTALS OVER EVERY MATCH, one compact strip (largest net first, four currencies; the rest on the Totals
                  link). Only from a read that answered; a short window says so in amber on the link below. */}
              {q.isSuccess && !refreshError && totals.length > 0 && (() => {
                // A SMALL TABLE, ONE ROW PER CURRENCY (Astra's audit P1/P3/P4, 1 Nov 2026: "AED · in … · out … · closing …" read as
                // one packed sentence): the currency, then In · Out · Closing in their own right-aligned seats, the figures at body
                // size in mono — the row's currency is their unit. On the phone each seat carries its word above its figure.
                const shown = [...totals].sort((a, b) => Math.abs(b.net) - Math.abs(a.net) || a.asset.localeCompare(b.asset)).slice(0, 4);
                const anyClosing = shown.some((t) => t.closing != null);
                // ONE COMPACT TABLE, shared heads (Astra, 1 Nov 2026): the currency, then In · Out — and Closing from 1024 (on the phone
                // three figures side by side cut the closing; it is on the Totals sheet below the list).
                const cols = anyClosing ? 'grid-cols-[3rem_repeat(2,minmax(0,1fr))] lg:grid-cols-[3.5rem_repeat(3,minmax(0,1fr))]' : 'grid-cols-[3rem_repeat(2,minmax(0,1fr))] lg:grid-cols-[3.5rem_repeat(2,minmax(0,1fr))]';
                const seat = (value: number | null, asset: string, lgOnly = false) => (
                  <span className={`min-w-0 text-right ${lgOnly ? 'hidden lg:block' : ''}`}>
                    {value == null ? <span className="text-tg-hint">—</span>
                      : <span className={`whitespace-nowrap font-mono tabular text-body ${value < 0 ? 'text-neg' : 'text-tg-text'}`}>{fmtAmount(value, asset)}</span>}
                  </span>
                );
                return (
                  <div aria-label="Totals" className="border-b border-stroke px-4 py-3 lg:px-5">
                    {/* A SHORT WINDOW SAYS SO WHERE THE FIGURES ARE (Astra, 1 Nov 2026: the caveat sat under the last record), with
                        the whole account in the Totals sheet. */}
                    {caveat && (
                      <p className="mb-2 flex items-center justify-between gap-3 text-meta text-warn">
                        <span>Totals over a short window</span>
                        <button type="button" className="tap-44 btn-link text-meta" onClick={() => setTotalsOpen(true)}>Why</button>
                      </p>
                    )}
                    <div className={`grid ${cols} gap-x-4 pb-1 text-label font-medium uppercase tracking-wider text-tg-hint`}>
                      <span /><span className="text-right">In</span><span className="text-right">Out</span>{anyClosing && <span className="hidden text-right lg:block">Closing</span>}
                    </div>
                    {shown.map((t) => (
                      <div key={t.asset} className={`grid ${cols} items-baseline gap-x-4 py-0.5`}>
                        <span className="text-body font-medium">{displayAsset(t.asset)}</span>
                        {seat(t.in, t.asset)}
                        {seat(t.out, t.asset)}
                        {anyClosing && seat(t.closing != null ? Number(t.closing) : null, t.asset, true)}
                      </div>
                    ))}
                  </div>
                );
              })()}
              {oneAccount
                ? <StatementRegister rows={rows} showBalance={statement} onOpen={openEntry} />
                : <MovementRegister rows={rows} onOpen={openEntry} showBalance={statement} />}
              {footer}
            </Panel>
          )}

          {/* AN EMPTY CLAIM NEEDS A SUCCESSFUL READ. */}
          {rows.length === 0 && q.isSuccess && (
            <Panel>
              <EmptyState
                className="lg:py-12"
                action={filtered ? <button type="button" onClick={clearAll} className="btn-link">Clear filters</button> : undefined}
              >
                {period.key === 'all' ? 'Nothing here.' : 'Nothing in this period.'}
              </EmptyState>
            </Panel>
          )}

          {/* A PARTIAL LIST SAYS SO: more is below until it is read, and a next page that failed says nothing after it was. */}
          {pagingError && (
            <ReadFailed what="more rows" error={pagingError} says="nothing after this row was read" retry={() => { void q.fetchNextPage(); }} className="mt-3" />
          )}

        </>
      )}

      {/* THE OPEN ENTRY, read by its id: the loaded row at once, then the books' answer (EntryView). */}
      {entryId && (
        <EntryView
          txn={entryId}
          loaded={selected}
          entity={entity}
          dealsById={dealsById}
          onOpenDeal={setDealId}
          onReversed={onReversed}
          // While the deal panel stands over the receipt, Escape is the deal panel's.
          onClose={() => { if (!dealId) closeEntry(); }}
        />
      )}
      {totalsOpen && <TotalsSheet totals={totals} caveat={caveat} onClose={() => setTotalsOpen(false)} />}
      {/* The trades log's own panel, over the movement (and over the receipt) that names the deal. */}
      <DealSlideOver deal={(dealId && dealsById.get(dealId)) || null} onClose={() => setDealId(null)} />
    </>
  );
}
